// Client-side image downscale + re-encode, run before an upload leaves the
// browser. A photo straight off a phone is routinely 15-25MB at 4000px+
// wide, which is pointless for something displayed a few hundred CSS pixels
// across: it wastes the user's upload bandwidth, our storage, and every
// future page load that renders it. Shrinking here means the small version
// is what actually gets stored, not a full-size original we downscale on
// the way out forever after.

// Long edge cap. Content columns in this app top out around 700 CSS px, so
// 1600 still covers a 2x display with headroom to spare.
const MAX_DIMENSION = 1600

// 0.82 is the usual sweet spot for photographic content: visually hard to
// separate from the original, but a fraction of the bytes.
const QUALITY = 0.82

// Below this an image is already cheap, and a re-encode would only throw
// away quality for a rounding error's worth of bytes.
const SKIP_IF_UNDER_BYTES = 400 * 1024

// Canvas flattens an animated GIF to a single frame, so these pass through
// untouched rather than being silently de-animated.
const PASSTHROUGH_TYPES = ['image/gif']

export function useImageCompression() {
  // Decodes to a bitmap. `imageOrientation: 'from-image'` matters for phone
  // photos: without it a portrait shot taken on a phone (which stores
  // landscape pixels plus an EXIF rotation flag) is drawn to the canvas
  // sideways, and re-encoding bakes that rotation in permanently.
  async function loadBitmap(file) {
    if (window.createImageBitmap) {
      return window.createImageBitmap(file, { imageOrientation: 'from-image' })
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')) }
      img.src = url
    })
  }

  // Resolves null when the browser can't produce the requested format.
  // Browsers that don't support a type don't error, they quietly hand back
  // PNG instead, so the type has to be checked rather than trusted.
  function encode(canvas, type) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob && blob.type === type ? blob : null), type, QUALITY)
    })
  }

  function swapExtension(filename, mimeType) {
    const ext = { 'image/webp': 'webp', 'image/jpeg': 'jpg', 'image/png': 'png' }[mimeType] || 'img'
    return `${filename.replace(/\.[^./\\]+$/, '') || 'image'}.${ext}`
  }

  /**
   * @param {File} file
   * @returns {Promise<File>} the smaller file, or the original whenever
   *   compressing it would not actually help. Never throws: an image this
   *   can't decode is handed back untouched for the server to accept or
   *   reject on its own terms.
   */
  async function compressImage(file) {
    if (!file?.type?.startsWith('image/')) return file
    if (PASSTHROUGH_TYPES.includes(file.type)) return file

    let bitmap
    try {
      bitmap = await loadBitmap(file)
    } catch {
      return file
    }

    const longEdge = Math.max(bitmap.width, bitmap.height)
    const scale = Math.min(1, MAX_DIMENSION / longEdge)

    // Already small enough in both dimensions and bytes: re-encoding would
    // be a pure quality loss.
    if (scale === 1 && file.size <= SKIP_IF_UNDER_BYTES) {
      bitmap.close?.()
      return file
    }

    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close?.()

    // WebP first: meaningfully smaller than JPEG at the same perceived
    // quality, and unlike JPEG it keeps an alpha channel, so a screenshot
    // with transparency survives.
    let blob = await encode(canvas, 'image/webp')

    if (!blob) {
      // JPEG has no alpha, and undrawn canvas pixels encode as black rather
      // than as nothing. Painting white underneath first means a
      // transparent PNG degrades to a white background instead of a black
      // one on the browsers that need this fallback.
      ctx.globalCompositeOperation = 'destination-over'
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, width, height)
      blob = await encode(canvas, 'image/jpeg')
    }

    // A small, already well-compressed source can come back *larger* after
    // a round trip through canvas. Keep whichever is actually smaller.
    if (!blob || blob.size >= file.size) return file

    return new File([blob], swapExtension(file.name, blob.type), { type: blob.type })
  }

  return { compressImage, MAX_DIMENSION }
}
